/**
 * ThemeToggle — Light/Dark switcher for the Vyvre platform.
 *
 * Behaviour:
 *   * Reads/writes localStorage key "strawhat-theme"
 *   * Applies data-theme="dark" (or removes it) on <html>
 *   * Defaults to "light" if no preference saved
 *   * Icon-only pill, 36×36, styled entirely via CSS variables so it looks
 *     correct in both themes
 */
import React, { useEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';

const STORAGE_KEY = 'strawhat-theme';

const applyTheme = (theme) => {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
};

export const ThemeToggle = () => {
  // Read once on mount; server-render safe because the effect below fixes it.
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const saved = (() => {
      try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
    })();
    const initial = saved === 'dark' ? 'dark' : 'light';
    setTheme(initial);
    applyTheme(initial);
  }, []);

  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    applyTheme(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  };

  const isDark = theme === 'dark';
  const Icon = isDark ? Sun : Moon;

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      className="inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors
        bg-[#F7F7F8] dark:bg-[#131518]
        border border-black/[0.08] dark:border-white/[0.08]
        text-[#6B6B6B] dark:text-[#8A8F98]
        hover:text-[#FF451A] hover:border-[#FF451A]
        dark:hover:text-[#FF451A] dark:hover:border-[#FF451A]"
      data-testid="theme-toggle"
    >
      <Icon size={15} strokeWidth={1.6} />
    </button>
  );
};
